import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import { FaPaperPlane, FaTimes, FaExclamationTriangle } from "react-icons/fa";
import GrapesEditor from "../../../components/GrapesJS/GrapesEditor";
import {
  normalizeBulkResult,
  renderTemplatePreview,
  eligibilityTone,
  reasonLabel,
} from "../pcInactivity";

/**
 * Shared outreach dialog: edit and save a re-engagement template, preview it,
 * send a test, and dry-run or send it to selected organizations.
 *
 * The PC inactivity tab and the three adoption-gap tabs use the same dialog
 * but different endpoints, copy and preview tokens, so all four are injected:
 *
 *   api        - { fetchTemplate, updateTemplate, sendTest, bulkSend }
 *   copy       - { sendLabel, templateBlurb, tokenHelp }
 *   buildTokens - (sampleOrg) => token map for the live preview
 *
 * Passing an empty `orgIds` opens it in template-only mode (no send controls).
 */
export default function OutreachModal({
  env,
  orgIds,
  orgNameById,
  sampleOrg,
  onClose,
  onSent,
  api,
  copy,
  buildTokens,
}) {
  const templateOnly = orgIds.length === 0;
  const [template, setTemplate] = useState(null);
  const [templateError, setTemplateError] = useState(null);
  const [subject, setSubject] = useState("");
  const [emailHtml, setEmailHtml] = useState("");
  const [emailPlainText, setEmailPlainText] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const [running, setRunning] = useState(null); // "preview" | "send" | null
  const [preview, setPreview] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const [showPreview, setShowPreview] = useState(false);
  const [testTo, setTestTo] = useState(
    () => getAuth()?.currentUser?.email || "",
  );
  const [testing, setTesting] = useState(false);
  const [testStatus, setTestStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.fetchTemplate(env)
      .then((json) => {
        if (cancelled) return;
        const tpl = json?.template || {};
        setTemplate(tpl);
        setSubject(tpl.subject || "");
        setEmailHtml(tpl.emailHtml || "");
        setEmailPlainText(tpl.emailPlainText || "");
      })
      .catch((err) => {
        if (!cancelled) {
          setTemplateError(err?.message || "Failed to load the email template");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [env, api]);

  const handleSaveTemplate = useCallback(async () => {
    if (!subject.trim() || !emailHtml.trim()) {
      setSaveError("Subject and email body are required.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const json = await api.updateTemplate(
        { subject, emailHtml, emailPlainText },
        env,
      );
      setTemplate(json?.template || null);
      setSavedAt(Date.now());
    } catch (err) {
      setSaveError(err?.message || "Failed to save the template");
    } finally {
      setSaving(false);
    }
  }, [subject, emailHtml, emailPlainText, env, api]);

  const handlePreview = useCallback(async () => {
    setRunning("preview");
    setActionError(null);
    setPreview(null);
    setConfirming(false);
    try {
      const json = await api.bulkSend(orgIds, { dryRun: true }, env);
      setPreview(json || null);
    } catch (err) {
      setActionError(err?.message || "Preview failed");
    } finally {
      setRunning(null);
    }
  }, [orgIds, env, api]);

  const handleSend = useCallback(async () => {
    setRunning("send");
    setActionError(null);
    try {
      const json = await api.bulkSend(orgIds, { dryRun: false }, env);
      setSendResult(json || null);
      setConfirming(false);
      onSent?.();
    } catch (err) {
      setActionError(err?.message || "Send failed");
    } finally {
      setRunning(null);
    }
  }, [orgIds, env, onSent, api]);

  const handleSendTest = useCallback(async () => {
    const to = testTo.trim();
    if (!to) {
      setTestStatus({ ok: false, message: "Enter an email address." });
      return;
    }
    setTesting(true);
    setTestStatus(null);
    try {
      await api.sendTest(
        { to, subject, emailHtml, emailPlainText },
        env,
      );
      setTestStatus({ ok: true, message: `Test email sent to ${to}.` });
    } catch (err) {
      setTestStatus({ ok: false, message: err?.message || "Test send failed" });
    } finally {
      setTesting(false);
    }
  }, [testTo, subject, emailHtml, emailPlainText, env, api]);

  const previewHtml = useMemo(() => {
    const tokens = buildTokens(sampleOrg);
    return renderTemplatePreview(emailHtml, tokens);
  }, [emailHtml, sampleOrg, buildTokens]);

  const previewSubject = useMemo(() => {
    const tokens = buildTokens(sampleOrg);
    return renderTemplatePreview(subject, tokens);
  }, [subject, sampleOrg, buildTokens]);

  const previewRows = useMemo(
    () => (preview?.results || []).map(normalizeBulkResult),
    [preview],
  );
  const sendRows = useMemo(
    () => (sendResult?.results || []).map(normalizeBulkResult),
    [sendResult],
  );

  const renderResults = (rows, summary) => (
    <div className="oo-bulk-results">
      <div className="oo-bulk-summary">
        <span className="oo-chip oo-chip--ok">
          {summary?.dryRun ? "Would send" : "Sent"}: {summary?.sent ?? 0}
        </span>
        <span className="oo-chip">Skipped: {summary?.skipped ?? 0}</span>
        {summary?.failed ? (
          <span className="oo-chip oo-chip--danger">
            Failed: {summary.failed}
          </span>
        ) : null}
        <span className="oo-chip">Targeted: {summary?.targeted ?? 0}</span>
      </div>
      <div className="oo-bulk-result-list">
        {rows.map((row) => (
          <div key={row.organizationId} className="oo-bulk-result-row">
            <span className="oo-bulk-result-org">
              {orgNameById[row.organizationId] || row.organizationId}
            </span>
            <span className={`oo-chip oo-chip--${eligibilityTone(row.reason)}`}>
              {row.sent
                ? `${summary?.dryRun ? "Would send" : "Sent"} · ${row.recipientCount}`
                : reasonLabel(row.reason)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="oo-modal-overlay" role="dialog" aria-modal="true">
      <div className="oo-modal">
        <div className="oo-modal-head">
          <h3>
            {templateOnly
              ? "Re-engagement email template"
              : `${copy.sendLabel} · ${orgIds.length} org(s)`}
          </h3>
          <button
            type="button"
            className="oo-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div className="oo-modal-body">
          {templateOnly ? (
            <div className="oo-preview-detail" style={{ marginBottom: 12 }}>
              {copy.templateBlurb}{" "}
              yourself a test. No customer emails are sent from here.
            </div>
          ) : (
            <div className="oo-bulk-warning">
              <FaExclamationTriangle /> This sends a real email to all active
              owners/admins of the {orgIds.length} selected org(s). Orgs in
              cooldown, deactivated, internal/test, or without a resolved
              owner/admin are skipped automatically.
            </div>
          )}

          {templateError ? (
            <div className="oo-form-errors">
              <div>
                <FaExclamationTriangle /> {templateError}
              </div>
            </div>
          ) : !template ? (
            <div className="oo-skel-stack">
              <div className="ou-skel" style={{ height: 40, marginBottom: 8 }} />
              <div className="ou-skel" style={{ height: 120 }} />
            </div>
          ) : (
            <>
              <div className="oo-field">
                <label>Email subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Subject line"
                />
              </div>

              <div className="oo-field">
                <label>
                  Email body — supports {"{{firstName}}"},{" "}
                  {copy.tokenHelp}{" "}
                  {"{{appUrl}}"}
                </label>
                <div className="oo-editor-wrap">
                  <GrapesEditor
                    initialContent={emailHtml}
                    onChange={(html) => setEmailHtml(html)}
                  />
                </div>
              </div>

              <div className="oo-field">
                <label>Plain-text fallback (optional)</label>
                <textarea
                  rows={4}
                  value={emailPlainText}
                  onChange={(e) => setEmailPlainText(e.target.value)}
                  placeholder="Plain-text version of the email"
                />
              </div>

              <div className="oo-preview-toolbar">
                <button
                  type="button"
                  className="oo-mini-btn"
                  onClick={() => setShowPreview((v) => !v)}
                >
                  {showPreview ? "Hide preview" : "See the email"}
                </button>
                <span className="oo-preview-detail">
                  Preview uses{" "}
                  {sampleOrg?.name ? `"${sampleOrg.name}"` : "sample"} values.
                </span>
              </div>

              {showPreview ? (
                <div className="oo-email-preview">
                  <div className="oo-email-preview-subject">
                    <strong>Subject:</strong> {previewSubject}
                  </div>
                  <iframe
                    className="oo-email-preview-frame"
                    title="Email preview"
                    srcDoc={previewHtml}
                  />
                </div>
              ) : null}

              <div className="oo-field oo-test-row">
                <label>Send yourself a test email</label>
                <div className="oo-test-controls">
                  <input
                    type="email"
                    value={testTo}
                    onChange={(e) => setTestTo(e.target.value)}
                    placeholder="you@addy-ai.com"
                  />
                  <button
                    type="button"
                    className="oo-mini-btn"
                    onClick={handleSendTest}
                    disabled={testing}
                  >
                    {testing ? "Sending…" : "Send test"}
                  </button>
                </div>
                {testStatus ? (
                  <div
                    className={`oo-preview-detail ${
                      testStatus.ok ? "" : "oo-test-error"
                    }`}
                  >
                    {testStatus.message}
                  </div>
                ) : null}
              </div>

              {saveError ? (
                <div className="oo-form-errors">
                  <div>
                    <FaExclamationTriangle /> {saveError}
                  </div>
                </div>
              ) : null}
              {savedAt ? (
                <div className="oo-preview-detail">Template saved.</div>
              ) : null}

              {actionError ? (
                <div className="oo-form-errors">
                  <div>
                    <FaExclamationTriangle /> {actionError}
                  </div>
                </div>
              ) : null}

              {sendResult
                ? renderResults(sendRows, sendResult.summary)
                : preview
                  ? renderResults(previewRows, preview.summary)
                  : null}
            </>
          )}
        </div>

        <div className="oo-modal-foot">
          <button
            type="button"
            className="oo-mini-btn"
            onClick={handleSaveTemplate}
            disabled={saving || !template || !!sendResult}
          >
            {saving ? "Saving…" : "Save template"}
          </button>
          <div className="oo-modal-foot-right">
            <button type="button" className="oo-mini-btn" onClick={onClose}>
              {sendResult ? "Close" : templateOnly ? "Done" : "Cancel"}
            </button>
            {!sendResult && !templateOnly ? (
              <>
                <button
                  type="button"
                  className="oo-mini-btn"
                  onClick={handlePreview}
                  disabled={!template || running === "preview"}
                >
                  {running === "preview" ? "Previewing…" : "Dry-run preview"}
                </button>
                {confirming ? (
                  <button
                    type="button"
                    className="oo-mini-btn oo-mini-btn--danger"
                    onClick={handleSend}
                    disabled={running === "send"}
                  >
                    {running === "send"
                      ? "Sending…"
                      : `Confirm — email ${orgIds.length} org(s)`}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="oo-mini-btn oo-mini-btn--primary"
                    onClick={() => setConfirming(true)}
                    disabled={!template || running != null}
                  >
                    <FaPaperPlane style={{ fontSize: 11 }} /> Send now
                  </button>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
